import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * Custom Route Reuse Strategy
 *
 * This strategy controls when Angular should reuse component instances during navigation.
 *
 * **Why this was added:**
 * By default, Angular reuses component instances when navigating to the same route with
 * different parameters. This optimization causes issues when components rely on route
 * parameters in their dependency injection setup (e.g., providers using `useFactory` with
 * `ActivatedRoute.snapshot.params`).
 *
 * **Problem it solves:**
 * When a component uses an injection token that reads route parameters via snapshot:
 * ```typescript
 * {
 *   provide: CONTEXT_METADATA_SCHEMA_KEY,
 *   useFactory: () => inject(ActivatedRoute).snapshot.params['schemaKey']
 * }
 * ```
 *
 * Navigating from `/path/schema1` to `/path/schema2` would reuse the component instance,
 * meaning the provider factory is never re-executed and the injected value remains stale.
 *
 * **Solution:**
 * This strategy forces Angular to destroy and recreate the component whenever ANY route
 * parameter changes, ensuring all providers are re-initialized with fresh parameter values.
 *
 * **Trade-offs:**
 * - Component state is lost on parameter changes (this is intentional)
 * - Slightly less performant than default strategy due to component recreation
 * - Component lifecycle hooks (ngOnInit, constructors) are called again
 */
export class CustomReuseStrategy implements RouteReuseStrategy {
  /**
   * Determines if a route should be detached and stored for later reuse.
   * @returns false - We never detach routes in this strategy
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  /**
   * Stores a detached route for later reuse.
   * Intentionally a no-op per Angular's `RouteReuseStrategy` contract:
   * since `shouldDetach` always returns `false` in this strategy, `store`
   * is never called by the router, and the signature must remain present
   * and empty to satisfy the interface.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {}

  /**
   * Determines if a previously detached route should be reattached.
   * @returns false - We never reattach routes in this strategy
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  /**
   * Retrieves a previously stored route.
   * @returns null - We never retrieve stored routes in this strategy
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return null;
  }

  /**
   * Determines if a route should be reused when navigating.
   *
   * Angular's default behavior reuses components when navigating to the same route
   * configuration, even if parameters differ. This method overrides that behavior.
   *
   * @param future - The future route snapshot (where we're navigating to)
   * @param curr - The current route snapshot (where we are now)
   * @returns true if the component should be reused, false if it should be recreated
   *
   * **Reuse criteria:**
   * A component is only reused if:
   * 1. The route configuration is identical (same component, path template, etc.)
   * 2. ALL route parameters are identical in both key and value
   *
   * If any parameter changes, the component is destroyed and recreated.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const routeShouldBeReused = this.calculateIfRouteShouldBeReused(future, curr);

    // Uncomment for debugging navigation decisions:
    // console.log('CustomReuseStrategy.shouldReuseRoute', future, curr, routeShouldBeReused);

    return routeShouldBeReused;
  }

  /**
   * Calculates whether a route should be reused based on route config and parameters.
   *
   * @param future - The target route snapshot
   * @param curr - The current route snapshot
   * @returns true if route can be reused, false otherwise
   *
   * **Logic:**
   * 1. Different route configs → don't reuse (different components)
   * 2. Different number of parameters → don't reuse
   * 3. Any parameter value differs → don't reuse
   * 4. All checks pass → reuse the component
   */
  private calculateIfRouteShouldBeReused(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    // Different route configurations mean different components
    if (future.routeConfig !== curr.routeConfig) {
      return false;
    }

    const futureParams = Object.keys(future.params);
    const currParams = Object.keys(curr.params);

    // Different number of parameters means the routes are structurally different
    if (futureParams.length !== currParams.length) {
      return false;
    }

    // Check if all parameter values are identical
    return futureParams.every(key => future.params[key] === curr.params[key]);
  }
}
